import * as React from 'react';
import { Conflict, CaptionTypography } from '@components/atoms';
import { AlertDialog, AlertDialogPrimaryLayout } from '@components/organisms';
import { useVersionPopup } from './hooks/useVersionPopup';

/**
 * Props for the VersionForceRefreshWrapper component.
 */
interface VersionRefreshWrapperProps {
	/**
	 * The class name to apply to the wrapper.
	 */
	className?: string;
	labels: {
		primaryActionLabel: string;
		title: string;
		description: string;
	};
	/**
	 * Optional callback when the popup is shown
	 */
	onPopupShow?: () => void;
	/**
	 * Optional callback when the popup is hidden
	 */
	onPopupHide?: () => void;
}

/**
 * A wrapper component that shows a force refresh popup when triggered via sessionStorage.
 * Uses the useVersionPopup hook for state management and event handling.
 * @param {VersionRefreshWrapperProps} props - The props for the VersionForceRefreshWrapper component.
 * @returns The wrapped content with conditional popup overlay.
 */
const VersionForceRefreshWrapper = ({
	labels,
	onPopupShow,
	onPopupHide,
}: VersionRefreshWrapperProps) => {
	const { isOpen, handleClose } = useVersionPopup({
		onPopupShow,
		onPopupHide,
	});

	return (
		<AlertDialog open={isOpen} onOpenChange={handleClose}>
			<AlertDialogPrimaryLayout
				title={labels.title}
				onPrimaryAction={handleClose}
				primaryButton={labels.primaryActionLabel}
				icon={<Conflict className="text-error" />}
			>
				<CaptionTypography truncate={false}>{labels.description}</CaptionTypography>
			</AlertDialogPrimaryLayout>
		</AlertDialog>
	);
};

export default VersionForceRefreshWrapper;
